import React, { createContext, useMemo, useState, useEffect } from 'react';
import { ThemeProvider, CssBaseline, createTheme } from '@mui/material';
import ToastStyles from './components/ToastStyles';

// Farm inspired palette.
//
// Olive is the brand, but a single olive cannot serve both modes: #556b2f is
// dark enough to read as a colour on cream and dark enough to vanish on a dark
// surface. So the brand olive lightens in dark mode — the same pigment with the
// sun on it — and the ink that sits on top of it flips to match.
const OLIVE = '#556b2f';
const OLIVE_LIT = '#9cb85f'; // sunlit grass: olive that survives a dark surface
const TAN = '#d2b48c';

// Dark mode is a field at dusk, not a grey control panel: a deep warm soil for
// the page, a lifted soil for cards, and cream rather than white for type.
const NIGHT = {
  page: '#191b15',
  surface: '#22251d',
  sunken: '#14160f',
  ink: '#ece7db',
  inkMuted: '#a9a496',
  line: 'rgba(236, 231, 219, 0.13)',
};

const getDesignTokens = (mode) => {
  const dark = mode === 'dark';

  return {
    palette: {
      mode,
      primary: {
        main: dark ? OLIVE_LIT : OLIVE,
        // Stated rather than computed, so a contained button is cream-on-olive
        // in daylight and soil-on-grass at night instead of whatever MUI's
        // contrast threshold happens to pick.
        contrastText: dark ? '#1a1d14' : '#f7f4e9',
      },
      secondary: { main: TAN, contrastText: '#1a1d14' },
      info: { main: dark ? '#6fb4e0' : '#4b9cd3' },
      warning: { main: dark ? '#e8bf5c' : '#e4b343' },
      // Barn red holds up on cream but turns to mud on soil, so it lifts to a
      // clay red where it has to be read against the dark.
      error: { main: dark ? '#e07a70' : '#b22222' },
      background: {
        default: dark ? NIGHT.page : '#faf9f6',
        paper: dark ? NIGHT.surface : '#ffffff',
      },
      text: {
        primary: dark ? NIGHT.ink : '#333333',
        secondary: dark ? NIGHT.inkMuted : '#555555',
      },
      divider: dark ? NIGHT.line : 'rgba(0, 0, 0, 0.12)',
      action: {
        ...(dark && {
          hover: 'rgba(236, 231, 219, 0.06)',
          selected: 'rgba(236, 231, 219, 0.10)',
        }),
      },
    },
    shape: { borderRadius: 12 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            // Firefox takes the pair directly; WebKit needs the rules below.
            scrollbarColor: dark ? `#3c4132 ${NIGHT.page}` : '#d8d4c8 transparent',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              width: 10,
              height: 10,
            },
            '&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track': {
              backgroundColor: 'transparent',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: 8,
              backgroundColor: dark ? '#3c4132' : '#d8d4c8',
            },
            '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
              backgroundColor: dark ? '#4e5641' : '#c2bcab',
            },
          },
        },
      },
      // The header paints its own surface, because it changes with scroll. All
      // this has to do is stop MUI from laying its default elevation tint under
      // the frosted glass, which would flatten it into a grey slab.
      MuiAppBar: {
        defaultProps: { elevation: 0, color: 'transparent' },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            // MUI lifts dark surfaces by laying a white film over them, which
            // drains the warmth out of the soil and leaves every card the same
            // shade of grey. The surface colour is already lighter than the
            // page, so the film only costs and never pays.
            ...(dark && { backgroundImage: 'none' }),
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            // Fields read as cut into the card rather than floating on it.
            ...(dark && { backgroundColor: NIGHT.sunken }),
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: dark ? 'rgba(236, 231, 219, 0.18)' : '#ccc',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: dark ? 'rgba(236, 231, 219, 0.34)' : '#888',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: dark ? OLIVE_LIT : OLIVE,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            ...(dark && {
              backgroundColor: '#33382a',
              color: NIGHT.ink,
              fontSize: '0.75rem',
            }),
          },
          arrow: {
            ...(dark && { color: '#33382a' }),
          },
        },
      },
    },
  };
};

export const ColorModeContext = createContext({ toggleColorMode: () => {}, mode: 'light' });

/** Freezes transitions so the whole page changes mode in one frame. */
const beginThemeSwitch = () => {
  document.documentElement.setAttribute('data-theme-switching', '');
};

export const ColorModeProvider = ({ children }) => {
  const [mode, setMode] = useState('light');

  useEffect(() => {
    const stored = localStorage.getItem('colorMode');
    if (stored !== 'light' && stored !== 'dark') return;
    // Restoring a stored dark mode flips the page a frame after first paint,
    // which fades just like the toggle does. The default is already light, so
    // only the restore that actually changes something needs freezing.
    if (stored !== 'light') beginThemeSwitch();
    setMode(stored);
  }, []);

  // Thaw only once React has committed the new palette and the browser has
  // painted it. Two frames: the first is the paint, the second is safely after
  // it. Keyed on mode so it cannot run before the swap it is releasing.
  useEffect(() => {
    const root = document.documentElement;
    if (!root.hasAttribute('data-theme-switching')) return undefined;
    let inner;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => root.removeAttribute('data-theme-switching'));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      mode,
      toggleColorMode: () => {
        beginThemeSwitch();
        setMode((prev) => {
          const next = prev === 'light' ? 'dark' : 'light';
          localStorage.setItem('colorMode', next);
          return next;
        });
      },
    }),
    [mode],
  );

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ToastStyles />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};
